import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useLocation } from 'react-router-dom';
import { DateTimeBadge } from './DateTimeBadge';
import {
  Home,
  FileText,
  Eye,
  Video,
  Users,
  Settings,
  User,
  Clock,
  MapPin,
  Calendar,
  BarChart3,
  CalendarDays,
  UserCheck,
  Upload,
  Film,
  Shield,
} from 'lucide-react';

// Map routes to page titles and icons
const pageConfig: Record<string, { title: string; icon: React.ElementType; subtitle?: string }> = {
  '/dashboard': { title: 'Dashboard', icon: Home, subtitle: 'Overview' },
  '/my-sessions': { title: 'My Sessions', icon: FileText, subtitle: 'Your daily hub' },
  '/calendar': { title: 'Calendar', icon: Calendar, subtitle: 'Schedule View' },
  '/appointments': { title: 'Appointments', icon: CalendarDays, subtitle: 'Manage Sessions' },
  '/clients': { title: 'Clients', icon: UserCheck, subtitle: 'Client Management' },
  '/session-notes': { title: 'Session Notes', icon: FileText, subtitle: 'RBT Documentation' },
  '/review-notes': { title: 'Supervisor Review', icon: Eye, subtitle: 'Review & Approve Notes' },
  '/review-notes-legacy': { title: 'Review Notes (Legacy)', icon: Eye, subtitle: 'Legacy Review Interface' },
  '/live-streams': { title: 'Live Streams', icon: Video, subtitle: 'Active Sessions' },
  '/location-audit': { title: 'Location Audit', icon: MapPin, subtitle: 'GPS Verification' },
  '/reports': { title: 'Analytics & Reports', icon: BarChart3, subtitle: 'Performance Metrics' },
  '/timesheets': { title: 'Timesheets', icon: Clock, subtitle: 'Time Tracking' },
  '/users': { title: 'User Management', icon: Users, subtitle: 'Team Administration' },
  '/settings': { title: 'Settings', icon: Settings, subtitle: 'System Configuration' },
  '/profile': { title: 'Profile', icon: User, subtitle: 'Account Settings' },
  '/upload': { title: 'Upload', icon: Upload, subtitle: 'File Upload' },
  '/footage': { title: 'Footage', icon: Film, subtitle: 'Video Library' },
  '/hr-dashboard': { title: 'HR Dashboard', icon: Users, subtitle: 'Human Resources' },
  '/security-settings': { title: 'Security', icon: Shield, subtitle: 'Security Settings' },
};

interface PageHeaderProps {
  // Optional overrides
  title?: string;
  subtitle?: string;
  icon?: React.ElementType;
  // Additional content to render on the right side
  actions?: React.ReactNode;
  // Hide the header completely for certain pages
  hidden?: boolean;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title: titleOverride,
  subtitle: subtitleOverride,
  icon: iconOverride,
  actions,
  hidden,
}) => {
  const { user } = useAuth();
  const { company } = useCompany();
  const location = useLocation();

  if (hidden) return null;

  // Get config for current page
  const config = pageConfig[location.pathname] || { title: 'VERA', icon: Shield };

  const title = titleOverride || config.title;
  const subtitle = subtitleOverride || config.subtitle;
  const Icon = iconOverride || config.icon;

  const styles = {
    header: {
      backgroundColor: '#4F6367',
      color: 'white',
      padding: '12px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      position: 'sticky' as const,
      top: 0,
      zIndex: 100,
    },
    left: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    iconContainer: {
      backgroundColor: 'rgba(255,255,255,0.1)',
      padding: '8px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    titleContainer: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    title: {
      fontSize: '18px',
      fontWeight: 'bold',
      margin: 0,
      lineHeight: 1.2,
      color: 'white', // explicit so global h1 styles can't darken it
    },
    subtitle: {
      fontSize: '12px',
      opacity: 0.8,
      margin: 0,
      color: 'white',
    },
    right: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    userInfo: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'flex-end',
      fontSize: '13px',
      color: 'white',
    },
    userName: {
      fontWeight: '600',
      color: 'white',
    },
    userRole: {
      fontSize: '11px',
      opacity: 0.8,
      textTransform: 'uppercase' as const,
      color: 'white',
    },
    companyName: {
      fontSize: '12px',
      opacity: 0.7,
      marginTop: '2px',
      color: 'white',
    },
  };

  return (
    <header className="page-header" style={styles.header}>
      <div style={styles.left}>
        <div style={styles.iconContainer}>
          <Icon size={24} color="white" />
        </div>
        <div style={styles.titleContainer}>
          <h1 className="page-header-title" style={styles.title}>{title}</h1>
          {subtitle && <p style={styles.subtitle}>{subtitle}</p>}
        </div>
      </div>

      <div style={styles.right}>
        {actions}
        <span className="page-header-datetime">
          <DateTimeBadge />
        </span>
        <div style={styles.userInfo}>
          <span style={styles.userName}>{user?.name || 'User'}</span>
          <span className="page-header-role" style={styles.userRole}>{user?.role} {user?.credentials && `• ${user.credentials}`}</span>
          {company && <span className="page-header-company" style={styles.companyName}>{company.name}</span>}
        </div>
      </div>
    </header>
  );
};
